/**
 * SSR-safe wrapper for jodit-react
 *
 * jodit-react accesses browser globals (self, window, document) during module
 * evaluation, which breaks server-side rendering. This wrapper provides:
 * - Server: Type-safe stubs that prevent runtime errors
 * - Client: Real jodit-react exports
 *
 * All imports of jodit-react should use this module instead:
 *   ❌ import { Jodit } from 'jodit-react';
 *   ✅ import { Jodit } from '@shared/core/jodit-react-ssr';
 */

/* eslint-disable no-restricted-imports */
// This file is the SSR wrapper, so it's allowed to import from jodit-react directly

// Re-export types
export type { IJoditEditorProps } from 'jodit-react';

// Import types for proper type annotations
import type JoditReactDefault from 'jodit-react';
import type { Jodit as JoditType } from 'jodit-react';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// Use proper types from jodit-react
let JoditEditor: typeof JoditReactDefault;
let JoditNamespace: typeof JoditType;

if (isBrowser) {
  // Client-side: re-export real jodit-react
  // We need dynamic require() here to avoid SSR issues with jodit-react
  // eslint-disable-next-line
  const joditReact = require('jodit-react');
  JoditEditor = joditReact.default;
  JoditNamespace = joditReact.Jodit;
} else {
  // Server-side: provide auto-stubbing Proxy to handle any Jodit API

  // Recursively create proxies that stub any property/method access
  // Returns stubs that can be chained, called, or used as objects safely
  // Generic allows proper typing at call site while maintaining runtime flexibility
  const createDeepStub = <T = unknown>(): T => {
    const stub = function () {
      return createDeepStub();
    };

    return new Proxy(stub, {
      get: (_target, prop) => {
        // Special case: Jodit.atom() is used to create reactive arrays
        // Example: Jodit.atom(['bold', 'italic']) in RichTextEditor.tsx:79
        // Return identity function to preserve config values during module load
        if (prop === 'atom') {
          return <T>(value: T): T => value;
        }

        // Return falsy values for common boolean/length checks
        if (prop === 'length') return 0;
        if (prop === 'toString') return () => '';
        if (prop === 'valueOf') return () => 0;

        // Return nested stub for any other property access
        return createDeepStub();
      },
      apply: (_target, _thisArg, _args) => {
        // Function calls return nested stub (allows chaining)
        return createDeepStub();
      },
      construct: (_target, _args) => {
        // Constructor calls return nested stub
        return createDeepStub();
      },
      // Make stub behave like an object
      has: () => true,
      ownKeys: () => [],
      getOwnPropertyDescriptor: () => ({ configurable: true, enumerable: true }),
    }) as T;
  };

  JoditNamespace = createDeepStub<typeof JoditType>();

  // Stub React component (won't render on server anyway due to useIsMounted guard)
  JoditEditor = (() => {
    if (__DEV__) {
      console.warn('JoditEditor rendered on server -- this should not be happen');
    }
    return null;
  }) as unknown as typeof JoditReactDefault;
}

// Export the properly typed values
export default JoditEditor;
export { JoditNamespace as Jodit };

// Re-export Jodit instance type for type annotations
// Import it separately to avoid conflicts with the value export above
import type { Jodit as JoditInstanceType } from 'jodit-react';

// Create an interface that merges with the value export
// This allows Jodit to be used as both a type and a value
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Jodit extends JoditInstanceType {}
